import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Autocomplete, Checkbox, Chip, TextField, Tooltip } from '@mui/material';
import { Field, FieldConfig, FieldProps } from 'formik';
import debounce from 'lodash/debounce';
// eslint-disable-next-line
import get from 'lodash/get';

import { CircularProgress } from './styled';

export interface ISearchSelectProps extends FieldConfig {
	loading?: boolean;
	label: string;
	data: any[];
	itemLabelKey?: string;
	onSearch?: (value: string) => void;
	onChange?: (value: any) => void;
	multiple?: boolean;
	disabled?: boolean;
	prepareValue?: (value: any) => any;
	prepareOption?: (option: any) => any; // @TODO: Is it possible typing
	placeholder?: string;
	getOptionLink?: (option: any) => string;
	getOptionLabel?: (option: any) => string;
	disableInput?: boolean;
	size?: 'small' | 'medium';
	required?: boolean;
	withSelectAllOption?: boolean;
	withTagTooltip?: boolean;
	tooltipContent?: (option: any) => React.ReactNode;
}

function SearchSelect({
	loading,
	label,
	data = [],
	itemLabelKey,
	onSearch,
	value,
	onChange,
	multiple,
	disabled,
	placeholder,
	prepareValue = (value) => value,
	getOptionLink,
	getOptionLabel,
	disableInput,
	size,
	required = false,
	withSelectAllOption = false,
	withTagTooltip = false,
	tooltipContent,
	...props
}: ISearchSelectProps) {
	const navigate = useNavigate();

	const handleSearch = (value: string) => {
		onSearch && onSearch(value);
	};

	// @TODO: errors, touched
	useEffect(() => {
		console.info('DEPRECATION: SearchField is deprecated. use Form.Field type="search" instead');
		handleSearch('');
	}, []);

	const handleTextChange = debounce((e) => {
		handleSearch(e.target.value);
	}, 1000);

	const getOptionLabelFinal = getOptionLabel || ((option: any) => get(option, itemLabelKey));
	const handleBlur = () => {
		// Reset filtered options when input loses focus
		handleSearch('');
	};
	return (
		<Field
			{...props}
			validate={(value: any) => {
				if (required && (value === null || (Array.isArray(value) && value.length === 0))) {
					return `The ${props?.name} field is required.`;
				}
				return undefined;
			}}
		>
			{({ field, form, meta }: FieldProps) => {
				// if we doesnt have a value:
				// For multiple setting - []
				// For !multiple setting - null

				// eslint-disable-next-line
				const fieldValue =
					// eslint-disable-next-line
					multiple && (field.value === null || field.value === undefined)
						? []
						: field.value === undefined
							? null
							: field.value;

				return (
					<Autocomplete
						value={prepareValue(value !== undefined ? value : fieldValue)}
						onChange={(_, newValue) => {
							const isSelectAll =
								withSelectAllOption && newValue.some((option: any) => option.id === 'all');
							const isAllItemsSelected =
								withSelectAllOption && form.values[field.name].length === data.length;

							let valueToSet = newValue;

							if (multiple && isSelectAll) {
								valueToSet = isAllItemsSelected ? [] : data;
							}

							onChange?.(valueToSet);
							form.setFieldValue(field.name, valueToSet);
						}}
						fullWidth
						size={size}
						multiple={multiple}
						options={
							withSelectAllOption && !loading ? [{ id: 'all', name: 'Select All' }, ...data] : data
						}
						loading={loading}
						getOptionLabel={getOptionLabelFinal}
						getOptionKey={(option) => option?.id || get(option, itemLabelKey)}
						filterOptions={onSearch ? (option) => option : undefined}
						isOptionEqualToValue={(option, value) => option === value || option?.id === value?.id}
						disabled={disabled}
						renderInput={(params) => (
							<TextField
								{...params}
								label={label}
								placeholder={placeholder}
								error={meta.touched && !!meta.error}
								helperText={meta.touched && !!meta.error ? meta.error : ''}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{!!loading && <CircularProgress />}
											{params.InputProps.endAdornment}
										</>
									),
								}}
								// eslint-disable-next-line
								inputProps={{ ...params.inputProps, readOnly: disableInput }}
								onChange={handleTextChange}
								onBlur={handleBlur}
							/>
						)}
						renderTags={
							(value, getTagProps) =>
								value.map((option, index) => {
									const { key, onDelete, ...tagProps } = getTagProps({ index });
									return (
										<span {...tagProps} key={key}>
											<Tooltip
												title={tooltipContent?.(option)}
												disableHoverListener={!withTagTooltip}
												disableFocusListener={!withTagTooltip}
												disableTouchListener={!withTagTooltip}
												slotProps={{
													tooltip: {
														sx: { maxWidth: 300 },
													},
												}}
												placement="top-end"
											>
												<Chip
													onClick={
														getOptionLink ? () => navigate(getOptionLink(option)) : undefined
													}
													label={getOptionLabelFinal(option)}
													onDelete={onDelete}
												/>
											</Tooltip>
										</span>
									);
								})
							// eslint-disable-next-line
						}
						renderOption={(optionProps, option, { selected }) => (
							<li {...optionProps}>
								{multiple && (
									<Checkbox checked={selected} size="small" sx={{ padding: 0, mr: '9px' }} />
								)}
								{getOptionLabelFinal(option)}
							</li>
						)}
					/>
				);
			}}
		</Field>
	);
}

export default SearchSelect;
